import { Pagination } from "services/search";

import { FleetVehiclePicture, FleetVehiclePictureRow } from "./types";

import { DataWithPagination } from "services/api/types";
import {
  transformFleetVehiclePicture,
  transformFleetVehiclePictureOnCreate,
  transformFleetVehiclePictureRow,
} from "./transformations";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";

/*---------------------------------------------------------------------------------------
  SEARCH
-----------------------------------------------------------------------------------------*/

export const getFleetVehiclePicturesAPI = async (
  vehicleId: string,
  pagination: Pagination,
  name?: string | null
): Promise<DataWithPagination<FleetVehiclePictureRow>> => {
  let path = `/v1/fleet/vehicles/images?vehicleId=${vehicleId}`;

  if (name) path += `&name=${name}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehiclePictureRow
  );
};

/*---------------------------------------------------------------------------------------
    INFO
  -----------------------------------------------------------------------------------------*/
export const getFleetVehiclePictureAPI = async (
  vehicleId: string,
  pictureId: string
): Promise<FleetVehiclePicture> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/pictures/${pictureId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehiclePicture(response.data);
};

/*---------------------------------------------------------------------------------------
    CREATE
-----------------------------------------------------------------------------------------*/

export const createVehiclePictureAPI = async (
  vehicleId: string,
  uploadId: string,
  name: string | null,
  description: string | null,
  t: (key: string) => string,
  hideNotification: boolean = false
): Promise<FleetVehiclePicture> => {
  const response = await createApiCall(
    {
      path: "/v1/fleet/vehicles/images/",
      method: "POST",
      body: transformFleetVehiclePictureOnCreate(
        vehicleId,
        uploadId,
        name,
        description
      ),
    },
    {
      getMessage: {
        success: () =>
          hideNotification ? "" : t("admin.fleet.vehicle.picture.create.message.success"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehiclePicture(response.data);
};

/*---------------------------------------------------------------------------------------
    DELETE 
-----------------------------------------------------------------------------------------*/
export const deleteVehiclePictureAPI = async (
  fleetVehicleImageId: string,
  t: (key: string) => string,
  silent: boolean = false
): Promise<FleetVehiclePicture> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/images/${fleetVehicleImageId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () =>
          silent ? "" : t("admin.fleet.vehicle.picture.create.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data.deleted;
};

/*---------------------------------------------------------------------------------------
    REORDER
-----------------------------------------------------------------------------------------*/
export const reorderVehiclePicturesAPI = async (
  vehicleId: string,
  orderedIds: string[],
  t: (key: string) => string,
  hideNotification: boolean = false
): Promise<void> => {
  await createApiCall(
    {
      path: "/v1/fleet/vehicles/images/reorder",
      method: "PUT",
      body: { vehicleId, orderedIds },
    },
    {
      getMessage: {
        success: () =>
          hideNotification ? "" : t("admin.fleet.vehicle.picture.reorder.message.success"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
};
